import {FlattenedSign, type JWK} from "jose";
import {debug, warn} from "./log";
import {get, set} from "./storage";

const SIGN_KEY_NAME = "sign";
const SIGN_KEY_PARAMS = {
    name: "ECDSA",
    namedCurve: "P-384"
}
const SIGN_ALG = "ES384";

interface StoredKey {
    privateKey: JWK;
    keyParams: typeof SIGN_KEY_PARAMS;
}

async function generateKey() {
    return await crypto.subtle.exportKey("jwk", (await crypto.subtle.generateKey(SIGN_KEY_PARAMS, true, ['sign'])).privateKey);
}

async function generateSignKey() {
    const data = {
        privateKey: await generateKey(),
        keyParams: SIGN_KEY_PARAMS
    } as StoredKey
    try {
        await set(SIGN_KEY_NAME, data);
    } catch (err) {
        warn("Failed to store signing key", err);
    }
    return data;
}

async function loadRawStoredKey(): Promise<StoredKey | null> {
    try {
        return await get(SIGN_KEY_NAME);
    } catch (err) {
        warn("Failed to load signing key from storage", err);
        return null;
    }
}

function extractPublicKey(privateKey: JWK): JWK {
    const publicKey = {...privateKey, key_ops: ['verify']};
    // Remove private key parts
    for (const prop of ['d', 'dp', 'dq', 'p', 'q', 'qi']) {
        delete publicKey[prop as keyof JWK];
    }
    return publicKey;
}

async function getPrivateKey() {
    try {
        const storedKey = await loadRawStoredKey();
        if (storedKey && typeof storedKey === 'object' && storedKey.keyParams && storedKey.keyParams.name === SIGN_KEY_PARAMS.name && storedKey.keyParams.namedCurve === SIGN_KEY_PARAMS.namedCurve && storedKey.privateKey) {
            return storedKey.privateKey;
        } else {
            warn("Stored signing key is invalid or outdated, generating a new one");
        }
    } catch (err) {
        warn("Error parsing stored signing key", err);
        // If there's an error parsing the stored key, we will generate a new one
    }
    return (await generateSignKey()).privateKey;
}

async function getPublicKey() {
    return extractPublicKey(await getPrivateKey());
}

export async function getSignKeyPublic() {
    return await getPublicKey();
}

export async function rotateSignKey() {
    await generateSignKey()
}

export async function signDataInline(data: unknown) {
    if (!data) {
        throw new Error("No data to sign");
    }
    if (!(data instanceof Uint8Array)) {
        // always JSON.stringify data (even strings)
        data = new TextEncoder().encode(JSON.stringify(data));
    }
    const key = await getPrivateKey();
    debug("Signing data with key:", {data, key});
    return await new FlattenedSign(data as Uint8Array).setProtectedHeader({alg: SIGN_ALG, b64: true}).sign(key)
}
